/**
 * Smart Location Routing Module
 * Handles automatic routing to collection pages based on detected/selected location
 * Shows "No Delivery" modal when location is not serviceable
 */

class LocationRouting {
  constructor() {
    // Region to collection URL mapping with full domain URLs
    this.collectionMapping = {
      // USA Regions (inspiredgo.com)
      us_west: 'https://inspiredgo.com/collections/usa',
      us_west_nv: 'https://inspiredgo.com/collections/usa', // Nevada/Utah/Arizona/Colorado also uses usa collection
      us_east: 'https://inspiredgo.com/collections/usaeast',

      // Canada Regions (inspiredgo.ca)
      // Region-group codes emitted by the tile selectors (West / Ontario / Quebec)
      ca_west: 'https://inspiredgo.ca/collections/west',
      ca_east: 'https://inspiredgo.ca/collections/east',
      // Granular province codes (kept for IP/postal-driven detection & scheduling)
      ca_bc: 'https://inspiredgo.ca/collections/west',
      ca_ab: 'https://inspiredgo.ca/collections/west',
      ca_sk: 'https://inspiredgo.ca/collections/west',
      ca_mb: 'https://inspiredgo.ca/collections/west',
      ca_on: 'https://inspiredgo.ca/collections/east',
      ca_qc: 'https://inspiredgo.ca/collections/quebec'
    };

    // Country to domain mapping for cross-domain redirects
    this.domainMapping = {
      US: 'inspiredgo.com',
      CA: 'inspiredgo.ca'
    };

    this.zapietLocationRegionMap = {
      '137796': 'ca_ab',
      '137798': 'ca_bc',
      '242677': 'ca_on',
      '270213': 'ca_on',
      '270994': 'ca_bc',
      '273216': 'us_west_nv',
      '276649': 'us_west_nv',
      '277960': 'us_west_nv',
      '278438': 'us_west',
      '279269': 'us_west',
      '285794': 'ca_on',
      '288292': 'us_west',
      '288949': 'us_west',
      '289774': 'ca_on',
      '319959': 'ca_qc',
      '299051': 'us_west_nv',
      '301750': 'us_west',
      '301948': 'us_east',
      '310459': 'us_east',
      '310466': 'us_east'
    }; 

    this.notifyEndpoint = 'https://script.google.com/macros/s/AKfycbzzR76RX9p_XEnHd_-iZsIfhkyhGaZrEjXGd11PsXOsKOoUzWy89BPVATMxcD6-I6Q9/exec';

    this.MODAL_DISMISSED_KEY = 'inspiredgo_no_delivery_dismissed';
    this.initialized = false;

    console.log('LocationRouting initialized');
  }

  /** Detect if running inside Shopify theme editor */
  isThemeEditor() {
    return !!(
      window.Shopify &&
      (window.Shopify.designMode || window.location.search.includes('preview_theme_id'))
    );
  }

  /**
   * Smart redirect: form-submission in theme editor (preserves context),
   * plain JS redirect in production.
   * @param {string} url     - Full target URL
   * @param {string} country - 'US' or 'CA'
   */
  performRedirect(url, country) {
    console.log('performRedirect:', { url, country, inEditor: this.isThemeEditor() });

    if (this.isThemeEditor()) {
      const urlObj = new URL(url);
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/localization';
      form.acceptCharset = 'UTF-8';

      const fields = {
        form_type: 'localization',
        utf8: '✓',
        _method: 'put',
        country_code: country,
        return_to: urlObj.pathname + urlObj.search
      };

      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } else {
      window.location.href = url;
    }
  }

  /**
   * Initialize the routing module
   * Only sets up event listeners - does NOT show modal on page load
   */
  async init() {
    if (this.initialized) return;
    this.initialized = true;

    console.log('LocationRouting init called');

    // Check if modal element exists
    const modal = document.getElementById('noDeliveryModal');
    console.log('No delivery modal element found:', !!modal);

    // Wait for location detector to be ready
    if (!window.locationDetector) {
      console.warn('Location detector not available');
      return;
    }

    console.log('Location detector available:', !!window.locationDetector);

    // Listen for location changes from dropdown
    document.addEventListener('locationChanged', (e) => {
      console.log('Location changed:', e.detail);

      // When user manually selects location, dismiss the modal
      this.dismissNoDeliveryModal();
      this.syncCollectionPostalConfirmation();

      // Check cross-domain redirect first (US ↔ CA domain switch)
      const redirected = this.handleCrossDomainRedirect(e.detail.country, e.detail.region);
      if (redirected) return;

      // Same domain - if on a collection page, redirect to the new location's collection
      this.handleCollectionPageRedirect(e.detail.country, e.detail.region);
    });

    // On default menu pages, auto-show region selector popup
    this.handleDefaultMenuPage();

    // Collection-page postal confirmation panel
    this.initCollectionPostalConfirmation();

    // Intercept product interactions if location not set/serviceable
    this.interceptProductInteractions();

    // On collection page load, check if redirect is needed
    await this.handleCollectionPageLoad();
  }

  /**
   * Check if the user has a valid serviceable location in session storage.
   * @returns {boolean}
   */
  hasServiceableLocation() {
    if (!window.locationDetector) return false;
    const cached = window.locationDetector.getCachedLocation();
    if (!cached || !cached.success || !cached.code || !cached.country) return false;
    return !!this.getCollectionUrl(cached.code);
  }

  /**
   * Intercept product interactions on collection pages when no serviceable location is set.
   *
   * Only blocks interactions when the user has NOT yet selected or been detected with
   * a serviceable location. If detection failed (API error), we do NOT block —
   * the user can browse freely.
   */
  interceptProductInteractions() {
    const currentPath = window.location.pathname.replace(/\/$/, '');
    if (currentPath !== '/collections/default-us-menu') return;

    const interactiveSelectors = [
      '.full-unstyled-link',
      '.quantity__button',
      '.quick-view',
      '.protein-pill',
      '.product-popup__cta-button',
      '#add-to-cart-btn',
      '.multi-add-btn',
      '#bulk-purchase',
      'quantity-input',
      '.quantity__input'
    ].join(',');

    document.addEventListener('click', (e) => {
      if (!e.isTrusted) return;

      const target = e.target.closest(interactiveSelectors);
      if (!target) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      this.openRegionModal('US');
    }, true);
  }

  /**
   * Auto-show region selector popup on default menu pages.
   * If a valid serviceable location is already in session storage, redirect directly.
   * Otherwise show the region selector popup.
   * /collections/default-ca-menu → show CA region popup
   * /collections/default-us-menu → show US region popup
   */
  handleDefaultMenuPage() {
    const path = window.location.pathname;
    if (path !== '/collections/default-ca-menu' && path !== '/collections/default-us-menu') return;

    // Stay on default menu pages. Product clicks will show the region selector
    // when the customer does not have a confirmed location.
    console.log('On default menu page; waiting for product interaction');
  }

  /**
   * Handle collection page load - show banner and check for mismatch.
   *
   * Three possible outcomes:
   *   1. Detection FAILED (success=false) → do nothing, let user browse normally
   *   2. Detection succeeded, user IS serviceable → check for menu mismatch
   *   3. Detection succeeded, user is NOT serviceable (confirmed) → show email capture
   *
   * The "We don't deliver" modal is ONLY shown when we have a confirmed
   * non-serviceable location (success=true, code=null). It is NEVER shown
   * when detection fails (API error, rate limit, ad blocker, etc.).
   */
  async handleCollectionPageLoad() {
    const currentPath = window.location.pathname;
    if (!currentPath.includes('/collections/')) return;

    // ,
    //   '/collections/default-ca-menu',
    //   '/collections/default-us-menu'
    // Only handle our known location-based collection paths
    const locationCollections = [
      '/collections/usa',
      '/collections/usaeast',
      '/collections/west',
      '/collections/east',
      '/collections/quebec'
    ];
    const isLocationCollection = locationCollections.some(c => currentPath === c || currentPath.startsWith(c + '/'));

    if (!isLocationCollection) return;

    if (!window.locationDetector) return;

    // Always show the banner on location-based collection pages
    this.showLocationMismatchBanner();

    // Check detected location against current page
    try {
      const location = await window.locationDetector.getLocation();

      // If detection failed entirely (API error, rate limit, network failure),
      // do nothing — let the user browse the page they're already on.
      if (!location.success) {
        console.log('Location detection failed, letting user browse normally:', location.error);
        return;
      }

      // Detection succeeded — check if user is in a serviceable region
      const isServiceable = location.code &&
        (location.country === 'US' || location.country === 'CA') &&
        this.getCollectionUrl(location.code);

      // Update collection heading with detected city if user is in a served region matching this page
      if (isServiceable && location.ipData && location.ipData.city) {
        const expectedUrl = this.getCollectionUrl(location.code);
        const expectedPath = new URL(expectedUrl).pathname;
        if (currentPath === expectedPath || currentPath.startsWith(expectedPath + '/')) {
          const heading = document.querySelector('.shogun-heading-component h1');
          if (heading) {
            heading.textContent = location.ipData.city + ' Menu';
          }
        }
      }

      if (isServiceable) {
        if (!this.locationMatchesCurrentDomain(location)) {
          console.log('Detected location is for another country/domain, leaving current menu in place:', location);
          return;
        }

        // Serviceable location — check if current menu matches
        const expectedUrl = this.getCollectionUrl(location.code);
        const expectedPath = new URL(expectedUrl).pathname;
        const expectedDomain = new URL(expectedUrl).hostname;
        const currentDomain = window.location.hostname;

        const domainMismatch = currentDomain !== expectedDomain && !currentDomain.includes('admin.shopify.com') && currentDomain.includes('inspiredgo');
        const pathMismatch = currentPath !== expectedPath && !currentPath.startsWith(expectedPath + '/');

        if (domainMismatch || pathMismatch) {
          // We no longer auto-suggest a menu switch from IP detection. The
          // customer's menu is chosen explicitly (postal code / region selector),
          // so an IP-based mismatch is not a reason to interrupt them.
          console.log('Serviceable mismatch (IP) — not prompting, detected:', location.code, 'expected:', expectedUrl);
          /*
          const currentMenuLabel = this.getMenuLabelFromPath(currentPath);
          const suggestedMenuLabel = this.getMenuLabelFromPath(expectedPath);
          this.openSwitchConfirmModal(currentMenuLabel, suggestedMenuLabel, expectedUrl, location.country, location.code);
          */
        }
      } else {
        /* HOTFIX: Non-serviceable location detected — do NOT auto-show modal.
        The "don't deliver" modal is only shown manually via the
        "Don't see your area?" link in the region selector popup.

        // Confirmed non-serviceable (success=true but region not in our service areas).
        // Show the email capture modal so we can learn where to expand.
        console.log('Confirmed non-serviceable location:', location.country, location.rawRegion);
        const detectedCity = (location.ipData && location.ipData.city) || '';
        const detectedRegion = location.rawRegion || '';
        const detectedLocationName = detectedCity && detectedRegion
          ? `${detectedCity}, ${detectedRegion}`
          : detectedCity || detectedRegion || '';
        this.openConfirmModal(detectedLocationName);
        */
      }
    } catch (e) {
      // On any error, fail silently — never block the user from browsing
      console.error('Error checking location:', e);
    }
  }
      // ,
      // '/collections/default-ca-menu',
      // '/collections/default-us-menu'
  isMenuCollectionPage(pathname = window.location.pathname) {
    const path = pathname || '';
    return [
      '/collections/usa',
      '/collections/usaeast',
      '/collections/west',
      '/collections/east',
      '/collections/quebec'
    ].some((prefix) => path === prefix || path.startsWith(prefix + '/'));
  }

  getCountryFromRegionCode(regionCode) {
    if (!regionCode) return null;
    return regionCode.indexOf('us_') === 0 ? 'US' : 'CA';
  }

  getCurrentDomainCountry() {
    const host = window.location.hostname;
    if (host === 'inspiredgo.com' || host.endsWith('.inspiredgo.com')) return 'US';
    if (host === 'inspiredgo.ca' || host.endsWith('.inspiredgo.ca')) return 'CA';
    return null;
  }

  locationMatchesCurrentDomain(location) {
    const domainCountry = this.getCurrentDomainCountry();
    return !domainCountry || !location || location.country === domainCountry;
  }

  getRegionFromZapietLocationId(locationId) {
    return this.zapietLocationRegionMap[String(locationId)] || null;
  }

  currentMenuMatchesRegion(regionCode) {
    const collectionUrl = this.getCollectionUrl(regionCode);
    if (!collectionUrl) return false;
    const expectedPath = new URL(collectionUrl).pathname;
    const currentPath = window.location.pathname;
    return currentPath === expectedPath || currentPath.startsWith(expectedPath + '/');
  }

  initCollectionPostalConfirmation() {
    const panel = document.getElementById('menuPostalConfirmation');
    this.menuPostalPanel = panel;
    if (panel) panel.hidden = true;
  }

  syncCollectionPostalConfirmation() {
    if (!this.menuPostalPanel) {
      this.menuPostalPanel = document.getElementById('menuPostalConfirmation');
    }
    if (!this.menuPostalPanel) return;
    if (!this.isMenuCollectionPage()) {
      this.menuPostalPanel.hidden = true;
      return;
    }

    let regionCode = null;
    let cachedZip = '';

    try {
      const cachedLocationId = sessionStorage.getItem('cs_location_id') || sessionStorage.getItem('menu_location_id');
      cachedZip = sessionStorage.getItem('cs_zip') || sessionStorage.getItem('menu_postal_code') || '';
      if (cachedLocationId) {
        regionCode = this.getRegionFromZapietLocationId(cachedLocationId);
      }
    } catch (e) {}

    if (!regionCode && window.locationDetector) {
      const cachedLocation = window.locationDetector.getCachedLocation();
      if (cachedLocation && cachedLocation.success && cachedLocation.code) {
        regionCode = cachedLocation.code;
      }
    }

    if (this.menuPostalInput && cachedZip) {
      this.menuPostalInput.value = cachedZip;
    }

    this.menuPostalPanel.hidden = false;
    this.resetCollectionPostalState();
  }

  resetCollectionPostalState() {
    if (this.menuPostalMessage) {
      this.menuPostalMessage.hidden = true;
      this.menuPostalMessage.textContent = '';
      this.menuPostalMessage.classList.remove('is-error', 'is-success');
    }
    if (this.menuPostalNotify) this.menuPostalNotify.hidden = true;
    if (this.menuPostalInputWrap) this.menuPostalInputWrap.classList.remove('is-error');
  }

  setCollectionPostalMessage(message, type = 'error') {
    if (!this.menuPostalMessage) return;
    this.menuPostalMessage.hidden = false;
    this.menuPostalMessage.textContent = message;
    this.menuPostalMessage.classList.remove('is-error', 'is-success');
    this.menuPostalMessage.classList.add(type === 'success' ? 'is-success' : 'is-error');
  }

  lookupCollectionPostalCode() {
    if (!this.menuPostalInput || !this.menuPostalSubmit) return;

    const zip = (this.menuPostalInput.value || '').trim();
    if (!zip) {
      if (this.menuPostalInputWrap) this.menuPostalInputWrap.classList.add('is-error');
      this.setCollectionPostalMessage('Please enter a postal / zip code.');
      return;
    }

    this.resetCollectionPostalState();
    this.menuPostalSubmit.disabled = true;
    this.menuPostalSubmit.textContent = 'checking...';

    const body = new URLSearchParams();
    body.append('geoSearchQuery', zip);
    body.append('shop', 'inspiredgo.myshopify.com');

    fetch('https://api.zapiet.com/v1.0/delivery/locations?shop=inspiredgo.myshopify.com', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded;' },
      body
    })
      .then((r) => r.json())
      .then((res) => {
        this.menuPostalSubmit.disabled = false;
        this.menuPostalSubmit.textContent = 'confirm my location';

        const regionCode = this.getRegionFromZapietLocationId(res && res.id);
        if (res.error || !res.id || !regionCode) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'non_serviceable_zip' });

          if (this.menuPostalInputWrap) this.menuPostalInputWrap.classList.add('is-error');
          this.setCollectionPostalMessage("Sorry, we currently don't deliver to this area; however, we're expanding quickly. Get notified when we cover your area.");
          if (this.menuPostalNotify) this.menuPostalNotify.hidden = false;
          try {
            sessionStorage.setItem('menu_postal_code', zip);
          } catch (e) {}
          return;
        }

        const country = this.getCountryFromRegionCode(regionCode);

        try {
          sessionStorage.setItem('menu_postal_code', zip);
          sessionStorage.setItem('menu_location_id', String(res.id));
          sessionStorage.setItem('cs_zip', zip);
          sessionStorage.setItem('cs_location_id', String(res.id));
        } catch (e) {}

        if (window.locationDetector) {
          window.locationDetector.setLocation(country, regionCode);
        }

        let targetUrl = this.getCollectionUrl(regionCode);
        if (!targetUrl) return;

        if (this.currentMenuMatchesRegion(regionCode) && !window.location.pathname.includes('/collections/default-')) {
          this.setCollectionPostalMessage('You’re on the right menu for your area.', 'success');
          this.menuPostalPanel.hidden = true;
          return;
        }

        try {
          const url = new URL(targetUrl);
          if (url.hostname !== window.location.hostname) {
            url.searchParams.set('cs_zip', zip);
            url.searchParams.set('cs_location_id', String(res.id));
            targetUrl = url.toString();
          }
        } catch (e) {}

        this.menuPostalSubmit.textContent = 'redirecting...';
        this.performRedirect(targetUrl, country);
      })
      .catch((error) => {
        console.error('Collection postal lookup failed:', error);
        this.menuPostalSubmit.disabled = false;
        this.menuPostalSubmit.textContent = 'confirm my location';
        if (this.menuPostalInputWrap) this.menuPostalInputWrap.classList.add('is-error');
        this.setCollectionPostalMessage('Something went wrong. Please try again.');
      });
  }

  submitCollectionNotifyRequest() {
    if (!this.menuPostalEmail || !this.menuPostalNotifyBtn || !this.menuPostalInput) return;

    const email = (this.menuPostalEmail.value || '').trim();
    const postal = (this.menuPostalInput.value || '').trim();

    if (!email) {
      this.menuPostalEmail.focus();
      return;
    }

    this.menuPostalNotifyBtn.disabled = true;
    this.menuPostalNotifyBtn.textContent = 'sending...';

    fetch(this.notifyEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ failedPostal: postal, customerEmail: email }),
      mode: 'no-cors',
      redirect: 'follow'
    })
      .then(() => {
        this.setCollectionPostalMessage('Thanks. We’ll let you know when we cover your area.', 'success');
        if (this.menuPostalNotify) this.menuPostalNotify.hidden = true;
      })
      .catch((error) => {
        console.error('Collection postal notify failed:', error);
        this.setCollectionPostalMessage('Thanks. We’ll let you know when we cover your area.', 'success');
        if (this.menuPostalNotify) this.menuPostalNotify.hidden = true;
      })
      .finally(() => {
        this.menuPostalNotifyBtn.disabled = false;
        this.menuPostalNotifyBtn.textContent = 'notify me';
      });
  }

  /**
   * Remove the legacy collection location banner so the progress bar stays standalone.
   */
  showLocationMismatchBanner() {
    const existingBanner = document.getElementById('locationMismatchBanner');
    if (existingBanner) existingBanner.remove();
    return;
    /*
    if (document.getElementById('locationMismatchBanner')) return;

    // Derive menu label from the collection path (not the domain, which can mismatch after redirects)
    const path = window.location.pathname;

    let menuLabel = '';
    if (path.includes('/collections/usaeast'))          menuLabel = 'East Coast USA';
    else if (path.includes('/collections/usa'))         menuLabel = 'West Coast USA';
    else if (path.includes('/collections/quebec'))      menuLabel = 'Quebec';
    else if (path.includes('/collections/east'))        menuLabel = 'Ontario';
    else if (path.includes('/collections/west'))        menuLabel = 'Western Canada';
    else if (path.includes('/collections/default-ca-menu')) menuLabel = 'Canada';
    else if (path.includes('/collections/default-us-menu')) menuLabel = 'USA';
    else                                                menuLabel = window.location.hostname.endsWith('inspiredgo.ca') ? 'Canada' : 'USA';

    const banner = document.createElement('div');
    banner.id = 'locationMismatchBanner';
    banner.className = 'region-bar';
    banner.hidden = true; // shown only when a zip is in session
    banner.innerHTML = `
      <span class="region-bar__text">📍 Delivering to <strong id="regionBarZipValue"></strong></span>
      <span class="separator">|</span>
      <button class="change-btn" id="regionBarZipChange" type="button">Not your location?</button>
    `;

    // Insert at the top of main content. When the expandable call-outs banner
    // (A/B variant) is present it sits between the hero and the grid, and the
    // location banner + progress bar unit must stay ABOVE it, so anchor there
    // instead of the grid. Falls back to the grid on the control theme.
    const menuElement =
      document.querySelector('.collection-callouts-section') ||
      document.getElementsByClassName('collection-grid-section')[0];
    if (menuElement) {
      menuElement.before(banner);
    }

    const progressBar = document.getElementById('progress-bar-header');
    if (progressBar && progressBar.classList.contains('pb-bar--collection')) {
      banner.after(progressBar);
      progressBar.classList.add('pb-bar--below-banner', 'pb-bar--with-location-banner');
      progressBar.classList.remove('pb-bar--in-cart', 'pb-bar--pending');
    }
    */
  }


  /**
   * Handle cross-domain redirect when user selects a region for a different domain
   * US regions → inspiredgo.com, CA regions → inspiredgo.ca
   * @param {string} country - Country code (US or CA)
   * @param {string} region - Region code
   * @returns {boolean} true if redirecting, false if same domain
   */
  handleCrossDomainRedirect(country, region) {
    const targetDomain = this.domainMapping[country];
    if (!targetDomain) return false;

    const currentHost = window.location.hostname;

    // Only handle cross-domain on production domains
    if (!currentHost.includes('inspiredgo')) return false;

    // Check if already on correct domain
    if (currentHost === targetDomain || currentHost.endsWith('.' + targetDomain)) {
      console.log('Already on correct domain:', currentHost);
      return false;
    }

    // Redirect to same page on correct domain, passing region via query params
    const params = new URLSearchParams(window.location.search);
    params.set('region', region);
    params.set('country', country);

    const newUrl = 'https://' + targetDomain + window.location.pathname + '?' + params.toString();
    console.log('Cross-domain redirect to:', newUrl);
    window.location.href = newUrl;
    return true;
  }

  /**
   * Show the no delivery modal
   * @param {Object} detectedLocation - The detected location data
   */
  showNoDeliveryModal(detectedLocation) {
    console.log('showNoDeliveryModal called with:', detectedLocation);

    const modal = document.getElementById('noDeliveryModal');
    console.log('Modal element:', modal);

    if (!modal) {
      console.error('No delivery modal element not found in DOM!');
      return;
    }

    // Update detected location text if available
    const detectedText = document.getElementById('noDeliveryDetectedLocation');
    if (detectedText && detectedLocation) {
      if (detectedLocation.city && detectedLocation.rawRegion) {
        detectedText.textContent = `We detected you're in ${detectedLocation.city}, ${detectedLocation.rawRegion}`;
      } else if (detectedLocation.countryName) {
        detectedText.textContent = `We detected you're in ${detectedLocation.countryName}`;
      } else {
        detectedText.textContent = 'We couldn\'t detect your location';
      }
    }

    console.log('Setting modal.hidden = false');
    // Show modal
    modal.hidden = false;
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
    console.log('Modal should now be visible');
  }

  /**
   * Dismiss the no delivery modal
   */
  dismissNoDeliveryModal() {
    const modal = document.getElementById('noDeliveryModal');
    if (modal) {
      modal.hidden = true;
      document.body.style.overflow = ''; // Restore scrolling
      // Mark as dismissed for this session
      sessionStorage.setItem(this.MODAL_DISMISSED_KEY, 'true');
    }
  }

  /**
   * Handle redirect when location changes on collection pages
   * @param {string} _country - Country code (US or CA) - unused but kept for event consistency
   * @param {string} region - Region code (e.g., 'us_west', 'ca_bc')
   */
  handleCollectionPageRedirect(_country, region) {
    // Check if we're on a collection page
    const currentPath = window.location.pathname;
    const isCollectionPage = currentPath.includes('/collections/');

    if (!isCollectionPage) {
      console.log('Not on collection page, skipping redirect');
      return;
    }

    console.log('On collection page, checking if redirect needed');

    // Get the collection URL for the new region
    const newCollectionUrl = this.getCollectionUrl(region);

    if (!newCollectionUrl) {
      console.warn('No collection URL found for region:', region);
      return;
    }

    // Extract the collection path from the URL
    const newCollectionPath = new URL(newCollectionUrl).pathname;
    const currentFullPath = window.location.pathname;

    // Check if we're already on the correct collection
    if (currentFullPath === newCollectionPath) {
      console.log('Already on correct collection page');
      return;
    }

    console.log('Redirecting from', currentFullPath, 'to', newCollectionUrl);

    // Redirect to the new collection
    window.location.href = newCollectionUrl;
  }

  /**
   * Get collection URL for a region
   * @param {string} regionCode - Region code (e.g., 'us_west', 'ca_bc')
   * @returns {string|null} Collection URL or null if not found
   */
  getCollectionUrl(regionCode) {
    return this.collectionMapping[regionCode] || null;
  }

  /**
   * Get the fallback URL when location is unknown/non-serviceable.
   * CA domain → /pages/locations
   * US domain → /collections/default-us-menu
   */
  getFallbackUrl() {
    const isCanada = window.location.hostname.endsWith('inspiredgo.ca');
    // return isCanada ? '/pages/locations' : '/collections/default-us-menu';
    return '/pages/locations';
  }

  /**
   * Route to collection page based on current location
   * Called when user clicks "Choose My Meals"
   * @returns {Promise<string>} Collection URL or fallback if no valid location
   */
  async routeToCollection() {
    const fallback = this.getFallbackUrl();

    if (!window.locationDetector) {
      console.warn('Location detector not available, routing to fallback');
      return fallback;
    }

    try {
      const location = await window.locationDetector.getLocation();

      // Check if location detection failed or region not serviceable
      const locationInvalid =
        !location.success ||
        !location.code ||
        location.confidence === 'none' ||
        location.error ||
        (location.country !== 'US' && location.country !== 'CA') ||
        !this.locationMatchesCurrentDomain(location);

      if (locationInvalid) {
        console.log('Location not detected or not serviceable, routing to fallback');
        return fallback;
      }

      // Get collection URL for region
      const collectionUrl = this.getCollectionUrl(location.code);

      if (!collectionUrl) {
        console.warn('No collection URL found for region:', location.code, ', routing to fallback');
        return fallback;
      }

      console.log('Routing to collection:', collectionUrl);
      return collectionUrl;
    } catch (error) {
      console.error('Error routing to collection:', error, ', routing to fallback');
      return fallback;
    }
  }

  /**
   * Handle "Choose My Meals" button click
   * Routes to appropriate collection or shows modal
   * @param {Event} event - Click event
   */
  async handleChooseMealsClick(event) {
    // Destinations that are already an explicit choice — let them navigate
    // normally instead of being re-routed to a region collection:
    //   /account      subscribers heading to the portal
    //   /locations    the manual location picker
    //   /get-started  the personalization quiz (CA and USA variants). The header
    //                 CTA points here on collection pages, so re-routing would
    //                 bounce the visitor back to a collection and never open the quiz.
    const link = event.target.closest('a');
    const passThrough = ['/account', '/locations', '/get-started'];
    if (link && passThrough.some(function (path) { return link.href.includes(path); })) {
      return;
    }

    // Prevent default navigation
    event.preventDefault();
    event.stopPropagation();

    // Get collection URL based on location
    const collectionUrl = await this.routeToCollection();

    if (collectionUrl) {
      // Navigate to collection
      window.location.href = collectionUrl;
    }
    // If no URL, modal is already shown
  }

  // ─── Region Selector Modal ────────────────────────────────────────────────

  openRegionModal(forceCountry) {
    const existing = document.getElementById('regionSelectorModal');
    if (existing && forceCountry && existing.dataset.country !== forceCountry) {
      existing.remove();
    }
    if (!document.getElementById('regionSelectorModal')) {
      this.createRegionModal(forceCountry);
    }
    const modal = document.getElementById('regionSelectorModal');
    // If the product modal dialog is open, move the overlay inside it (top layer)
    const productDialog = document.getElementById('modal');
    const targetParent = (productDialog && productDialog.open) ? productDialog : document.body;
    if (modal.parentNode !== targetParent) {
      targetParent.appendChild(modal);
    }
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  closeRegionModal() {
    const modal = document.getElementById('regionSelectorModal');
    if (modal) {
      modal.hidden = true;
      document.body.style.overflow = '';
      // Move back to body so it's ready for next trigger
      if (modal.parentNode !== document.body) {
        document.body.appendChild(modal);
      }
    }
    this._regionSelectHook = null;
  }

  handleRegionModalSelection(country, region, collectionUrl) {
    const hook = this._regionSelectHook;
    const proceed = () => {
      if (window.locationDetector) {
        window.locationDetector.setLocation(country, region);
      }
      this.closeRegionModal();
      this.performRedirect(collectionUrl, country);
    };

    // A hook can intercept the selection (e.g. to clear the cart first) and
    // decide when to continue; with no hook, proceed immediately.
    if (typeof hook === 'function') {
      hook({ country, region, collectionUrl, proceed });
    } else {
      proceed();
    }
  }

  createRegionModal(forceCountry) {
    const isCanada = forceCountry
      ? forceCountry === 'CA'
      : window.location.hostname.endsWith('inspiredgo.ca');

    // Region images: use theme settings if uploaded, otherwise fall back to CDN defaults
    const ts = window.themeSettings || {};
    const cdnBase = 'https://cdn.shopify.com/s/files/1/0266/9900/5994/files/';
    // Desktop images
    const regionImages = {
      us_west: ts.region_image_us_west_desktop || (cdnBase + '1_83d838df-1f0f-4892-ac22-bb2141460cea.jpg'),
      us_east: ts.region_image_us_east_desktop || 'https://cdn.shopify.com/s/files/1/0266/9900/5994/files/2_20c3a3be-ba2b-4c9d-b723-e2bfae514028.jpg?v=1772746632',
      ca_west: ts.region_image_ca_west_desktop || (cdnBase + 'ca-region-west.png'),
      // theme.liquid always provides the Ontario.png / Quebec.png theme assets; CDN files are last-resort fallbacks
      ca_east: ts.region_image_ca_east_desktop || (cdnBase + 'ca-region-east.png'),
      ca_qc: ts.region_image_ca_qc_desktop || (cdnBase + 'ca-region-east.png')
    };
    // Mobile images
    const mobileRegionImages = {
      us_west: ts.region_image_us_west_mobile || null,
      us_east: ts.region_image_us_east_mobile || null,
      ca_west: ts.region_image_ca_west_mobile || null,
      ca_east: ts.region_image_ca_east_mobile || null,
      ca_qc: ts.region_image_ca_qc_mobile || null
    };

    const usRegions = [
      { code: 'us_west', country: 'US', name: 'West', url: 'https://inspiredgo.com/collections/usa' },
      { code: 'us_east', country: 'US', name: 'East', url: 'https://inspiredgo.com/collections/usaeast' }
    ];

    const caRegions = [
      { code: 'ca_west', country: 'CA', name: 'west', desc: 'BC, AB, SK, MB', url: 'https://inspiredgo.ca/collections/west' },
      { code: 'ca_east', country: 'CA', name: 'ontario', desc: 'ON', url: 'https://inspiredgo.ca/collections/east' },
      { code: 'ca_qc', country: 'CA', name: 'quebec', desc: 'QC', url: 'https://inspiredgo.ca/collections/quebec' }
    ];

    const regions = isCanada ? caRegions : usRegions;

    let cardsHtml;
    if (isCanada) {
      cardsHtml = regions.map(r => {
        const mobileImg = mobileRegionImages[r.code];
        const mobileSource = mobileImg ? `<source media="(max-width: 540px)" srcset="${mobileImg}">` : '';
        return `
        <div class="rsm-card rsm-card--ca-img" tabindex="0" role="button" aria-label="${r.name} — ${r.desc.replace(/<br>/g, ', ')}" data-region="${r.code}" data-country="${r.country}" data-url="${r.url}">
          <picture>
            ${mobileSource}
            <img src="${regionImages[r.code]}" alt="${r.name}" loading="lazy">
          </picture>
          <div class="rsm-card__overlay">
            <span class="rsm-card__code">${r.name}</span>
            <span class="rsm-card__desc" translate="no" data-wg-notranslate>${r.desc}</span>
          </div>
        </div>`;
      }).join('');
    } else {
      cardsHtml = regions.map(r => {
        const mobileImg = mobileRegionImages[r.code];
        const mobileSource = mobileImg ? `<source media="(max-width: 540px)" srcset="${mobileImg}">` : '';
        return `
        <div class="rsm-card rsm-card--us-img" tabindex="0" role="button" aria-label="${r.name} region" data-region="${r.code}" data-country="${r.country}" data-url="${r.url}">
          <picture>
            ${mobileSource}
            <img src="${regionImages[r.code]}" alt="${r.name}" loading="lazy">
          </picture>
        </div>`;
      }).join('');
    }

    // const footerHtml = !isCanada
    //   ? `<div class="rsm-footer"><a href="https://inspiredgo.ca/collections/default-ca-menu">🇨🇦 looking for our canadian menu?</a></div>`
    //   : `<div class="rsm-footer"><a href="https://inspiredgo.com/collections/default-us-menu">🇺🇸 looking for our US menu?</a></div>`;

    const footerHtml = !isCanada
      ? `<div class="rsm-footer"><a href="https://inspiredgo.ca/pages/locations">🇨🇦 looking for our canadian menu?</a></div>`
      : `<div class="rsm-footer"><a href="https://inspiredgo.com/pages/locations">🇺🇸 looking for our US menu?</a></div>`;


    const modal = document.createElement('div');
    modal.id = 'regionSelectorModal';
    modal.className = 'rsm-overlay';
    modal.dataset.country = isCanada ? 'CA' : 'US';
    modal.hidden = true;
    modal.innerHTML = `
      <div class="rsm-modal${isCanada ? ' rsm-modal--ca' : ''}" role="dialog" aria-modal="true" aria-label="Select your delivery region">
        <button class="rsm-close" type="button" aria-label="Close"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="4" y1="4" x2="16" y2="16"/><line x1="16" y1="4" x2="4" y2="16"/></svg></button>
        <div class="rsm-icon">📍</div>
        <h2 class="rsm-title">select your delivery region</h2>
        <p class="rsm-subtitle">Choose the region closest to you so we can show the right menu.</p>
        <div class="rsm-cards rsm-cards--${isCanada ? 'ca' : 'us'}">${cardsHtml}</div>
        ${footerHtml}
        <div class="rsm-no-area"><a href="#" class="rsm-no-area__link">🤔 Don't see your area? Click here</a></div>
      </div>
    `;

    // "Don't see your area?" link → close RSM, open confirm modal
    modal.querySelector('.rsm-no-area__link').addEventListener('click', (e) => {
      e.preventDefault();
      this.closeRegionModal();
      sessionStorage.removeItem('inspiredgo_confirm_dismissed');
      this.openConfirmModal('');
    });

    // Bind region card clicks and keyboard activation
    modal.querySelectorAll('.rsm-card').forEach(card => {
      const selectCard = () => {
        const country = card.dataset.country;
        const region  = card.dataset.region;
        const url     = card.dataset.url;
        console.log('Region selected from modal:', country, region, url);
        this.handleRegionModalSelection(country, region, url);
      };
      card.addEventListener('click', selectCard);
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectCard();
        }
      });
    });

    // Close button
    modal.querySelector('.rsm-close').addEventListener('click', () => this.closeRegionModal());

    // Close on overlay backdrop click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) this.closeRegionModal();
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeRegionModal();
    });

    document.body.appendChild(modal);
  }

  // ─── Confirm Location Modal (auto-shown on mismatch) ───────────────────────

  openConfirmModal(detectedLocationName) {
    // Don't show if already dismissed this session
    if (sessionStorage.getItem('inspiredgo_confirm_dismissed')) return;

    if (!document.getElementById('confirmLocationModal')) {
      this.createConfirmModal(detectedLocationName);
    }
    document.getElementById('confirmLocationModal').hidden = false;
    document.body.style.overflow = 'hidden';
  }

  closeConfirmModal() {
    const modal = document.getElementById('confirmLocationModal');
    if (modal) {
      modal.hidden = true;
      document.body.style.overflow = '';
      sessionStorage.setItem('inspiredgo_confirm_dismissed', 'true');
    }
  }

  createConfirmModal(detectedLocationName) {
    const modal = document.createElement('div');
    modal.id = 'confirmLocationModal';
    modal.className = 'rcm-overlay';
    modal.hidden = true;

    const locationLine = detectedLocationName
      ? `<p class="rcm-detected">We think you are in <strong>${detectedLocationName}</strong></p>`
      : '';

    modal.innerHTML = `
      <div class="rcm-modal" role="dialog" aria-modal="true" aria-label="We don't deliver to your area yet">
        <button class="rcm-close" type="button" aria-label="Close">&times;</button>
        <h2 class="rcm-title">We don't deliver to your area yet</h2>
        ${locationLine}
        <p class="rcm-subtitle">BUT WE ARE EXCITED TO EXPAND!</p>
        <div class="rcm-form">
          <label class="rcm-field__label" for="rcmCityInput">What postal code, city, or location would you like Inspired Go to expand to next?</label>
          <input class="rcm-field__input" type="text" id="rcmCityInput" placeholder="Enter city or location" value="${detectedLocationName}" />

          <p class="rcm-note">We will let you know when delivery is an option.</p>

          <label class="rcm-field__label" for="rcmEmailInput">Please leave us your email:</label>
          <input class="rcm-field__input" type="email" id="rcmEmailInput" placeholder="your@email.com" />

          <button class="rcm-continue" type="button" id="rcmSubmitBtn">Submit</button>
        </div>
      </div>
    `;

    const submitBtn = modal.querySelector('#rcmSubmitBtn');

    submitBtn.addEventListener('click', () => {
      const city = modal.querySelector('#rcmCityInput').value.trim();
      const email = modal.querySelector('#rcmEmailInput').value.trim();

      if (!email) {
        modal.querySelector('#rcmEmailInput').focus();
        return;
      }

      // Show loading state
      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting...';

      // Post to the same Google Sheet endpoint used by the cart postal code flow
      fetch('https://script.google.com/macros/s/AKfycbzzR76RX9p_XEnHd_-iZsIfhkyhGaZrEjXGd11PsXOsKOoUzWy89BPVATMxcD6-I6Q9/exec', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ failedPostal: city, customerEmail: email }),
        mode: 'no-cors',
        redirect: 'follow'
      })
        .then(() => console.log('Non-serviceable area email submitted:', { city, email }))
        .catch(error => console.error('Error submitting email:', error))
        .finally(() => {
          submitBtn.textContent = 'Thank you!';
          setTimeout(() => this.closeConfirmModal(), 1200);
        });
    });

    // Close button
    modal.querySelector('.rcm-close').addEventListener('click', () => this.closeConfirmModal());

    // Close on overlay backdrop click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) this.closeConfirmModal();
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeConfirmModal();
    });

    document.body.appendChild(modal);
  }

  // ─── Switch Confirm Modal (serviceable mismatch) ────────────────────────────

  openSwitchConfirmModal(currentMenuLabel, suggestedMenuLabel, redirectUrl, country, regionCode) {
    if (sessionStorage.getItem('inspiredgo_switch_dismissed')) return;

    if (!document.getElementById('switchConfirmModal')) {
      this.createSwitchConfirmModal(currentMenuLabel, suggestedMenuLabel, redirectUrl, country, regionCode);
    }
    document.getElementById('switchConfirmModal').hidden = false;
    document.body.style.overflow = 'hidden';
  }

  closeSwitchConfirmModal() {
    const modal = document.getElementById('switchConfirmModal');
    if (modal) {
      modal.hidden = true;
      document.body.style.overflow = '';
      sessionStorage.setItem('inspiredgo_switch_dismissed', 'true');
    }
  }

  /**
   * Get a human-readable menu label from a collection path
   */
  getMenuLabelFromPath(path) {
    if (path.includes('/collections/usaeast'))          return 'East Coast USA';
    if (path.includes('/collections/usa'))              return 'West Coast USA';
    if (path.includes('/collections/quebec'))           return 'Quebec';
    if (path.includes('/collections/east'))             return 'Ontario';
    if (path.includes('/collections/west'))             return 'Western Canada';
    // if (path.includes('/collections/default-ca-menu'))  return 'Canada';
    // if (path.includes('/collections/default-us-menu'))  return 'USA';
    return '';
  }

  createSwitchConfirmModal(currentMenuLabel, suggestedMenuLabel, redirectUrl, country, regionCode) {
    const modal = document.createElement('div');
    modal.id = 'switchConfirmModal';
    modal.className = 'scm-overlay';
    modal.hidden = true;
    modal.innerHTML = `
      <div class="scm-modal" role="dialog" aria-modal="true" aria-label="Switch location">
        <button class="scm-close" type="button" aria-label="Close">&times;</button>
        <p class="scm-text">Looks like you're viewing the <strong>${currentMenuLabel}</strong> menu. Would you like to switch to the <strong>${suggestedMenuLabel}</strong> menu?</p>
        <div class="scm-actions">
          <button class="scm-btn scm-btn--confirm" type="button">Switch to ${suggestedMenuLabel}</button>
          <button class="scm-btn scm-btn--cancel" type="button">Stay on ${currentMenuLabel}</button>
        </div>
      </div>
    `;

    // Confirm → save location + redirect
    modal.querySelector('.scm-btn--confirm').addEventListener('click', () => {
      if (window.locationDetector) {
        window.locationDetector.setLocation(country, regionCode);
      }
      this.closeSwitchConfirmModal();
      this.performRedirect(redirectUrl, country);
    });

    // Cancel → dismiss
    modal.querySelector('.scm-btn--cancel').addEventListener('click', () => this.closeSwitchConfirmModal());

    // Close X button
    modal.querySelector('.scm-close').addEventListener('click', () => this.closeSwitchConfirmModal());

    // Close on overlay backdrop click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) this.closeSwitchConfirmModal();
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeSwitchConfirmModal();
    });

    document.body.appendChild(modal);
  }
}

// Initialize and expose globally
window.LocationRouting = LocationRouting;
window.locationRouting = new LocationRouting();

// Debug helper - force show modal (for testing)
window.testShowNoDeliveryModal = function() {
  console.log('Force showing no delivery modal for testing');
  if (window.locationRouting) {
    window.locationRouting.showNoDeliveryModal({
      success: true,
      country: 'PK',
      countryName: 'Pakistan',
      city: 'Karachi',
      rawRegion: 'Sindh',
      confidence: 'none',
      error: 'Country not serviced'
    });
  }
};

// Initialize when DOM is ready AND location detector is available
function initializeRouting() {
  if (window.locationDetector) {
    console.log('Location detector ready, initializing routing');
    window.locationRouting.init();
    setupChooseMealsButton();
  } else {
    console.log('Location detector not ready yet, retrying in 100ms');
    // Retry after a short delay to allow location-detection.js to load
    setTimeout(initializeRouting, 100);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeRouting);
} else {
  initializeRouting();
}

/**
 * Setup "Choose My Meals" button click handler
 * Intercepts clicks and routes based on location
 */
function setupChooseMealsButton() {
  const chooseMealsLink = document.getElementById('chooseMealsLink');

  if (chooseMealsLink) {
    chooseMealsLink.addEventListener('click', async (e) => {
      // Check if this is a subscriber going to account page
      const href = chooseMealsLink.getAttribute('href');
      if (href && href.includes('/pages/portal')) {
        // Let subscribers go to account page normally
        return;
      }

      // For non-subscribers, use smart routing
      if (window.locationRouting) {
        await window.locationRouting.handleChooseMealsClick(e);
      }
    });
  }
}
